import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IDispatchManufactoring } from "../models/dispatchManufactoring";
import { IDispatchManufactoringApiResponse } from "../models/api/dispatchManufactoringApiResponse";
import { IDispatchManufactoringForm } from "../models/forms/dispatchManufactoringForm";
import { IDispatchManufactoringFilter } from "../models/filters/dispatchManufactoringFilter";
import DispatchManufactoringProductionServices from "../services/DispatchManufactoringService";
import { IPrevisionalDispatch } from "../models/previsionalDispatch";
import { IParameter } from "../models/parameter";
import { IManufactoringProduction } from "../models/manufactoringProduction";
import { ICenter } from "../models/center";
import { ILot } from "../models/lot";
import { GroupedStock } from "../helper/type";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { useCenterStore } from "./center.store";
import { useParameterStore } from "./parameter.store";
import { useLotStore } from "./lot.store";
import { useManufactoringProductionStore } from "./manufactoringProduction.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import i18n from "@/i18n";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";

export const useDispatchManufactoringStore = defineStore("DispatchManufactoring", () => {
    // State
    const isLoading = ref<boolean>(false);
    const datas = ref<IDispatchManufactoring[]>([]);
    const current = ref<IDispatchManufactoring>({} as IDispatchManufactoring);
    const itemForm = ref<IDispatchManufactoringForm>({} as IDispatchManufactoringForm);
    const filter = ref<IDispatchManufactoringFilter>({} as IDispatchManufactoringFilter);
    const recentOnly = ref<boolean>(false);
    const loadedCenterId = ref<number | undefined>(undefined);
    // true si datas a été chargé via loadEjaculateData (composite), false si via load() direct
    const loadedViaComposite = ref(false);
    // Fenetre de dates reellement couverte par datas (undefined = pas de borne).
    const loadedDateFrom = ref<Date | undefined>(undefined);

    // Données prévisionnelles pré-résolues par le backend (pas d'hydratation)
    const previsionalDatas = ref<IPrevisionalDispatch[]>([]);
    const previsionalLoadedCenterId = ref<number | undefined>(undefined);

    // Compteur incrémenté après chaque batch SignalR — permet aux computed externes
    // de forcer une réévaluation quand les dispatches changent via SignalR
    const signalRVersion = ref(0);

    // Actions - Hydration
    /**
     * Hydrate DispatchManufactorings from API response
     */
    async function hydrateDispatchManufactorings(dispatches: IDispatchManufactoringApiResponse[], centerId?: number, dateFrom?: Date, perfPage?: string): Promise<IDispatchManufactoring[]> {
        if (dispatches.length === 0) return [];
        const { start: perfStart, end: perfEnd } = usePerformanceTracker(perfPage);

        // 1. Get stores
        const centerStore = useCenterStore();
        const parameterStore = useParameterStore();
        const lotStore = useLotStore();
        const manufactoringProductionStore = useManufactoringProductionStore();

        // 2. Load in parallel if needed
        // Si un store est en cours de chargement, attendre qu'il finisse au lieu de skip
        const waitForStore = (store: { isLoading: boolean }, timeoutMs = 10000) =>
            new Promise<void>((resolve, reject) => {
                if (!store.isLoading) return resolve();
                const start = Date.now();
                const interval = setInterval(() => {
                    if (!store.isLoading) { clearInterval(interval); resolve(); }
                    else if (Date.now() - start > timeoutMs) { clearInterval(interval); resolve(); }
                }, 50);
            });

        const loadPromises: Promise<unknown>[] = [];
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        const lotNeedsLoad =
            lotStore.datas.length === 0 ||
            (dateFrom === undefined && lotStore.loadedDateFrom !== undefined) ||
            (dateFrom !== undefined && lotStore.loadedDateFrom !== undefined && lotStore.loadedDateFrom > dateFrom);
        if (lotNeedsLoad) {
            if (lotStore.isLoading) {
                loadPromises.push(waitForStore(lotStore));
            } else {
                loadPromises.push(lotStore.load({ page: 1, pageSize: 0, centerId, dateFrom }, centerId, perfPage));
            }
        }
        const mfpNeedsLoad =
            manufactoringProductionStore.datas.length === 0 ||
            (dateFrom === undefined && manufactoringProductionStore.loadedDateFrom !== undefined) ||
            (dateFrom !== undefined && manufactoringProductionStore.loadedDateFrom !== undefined && manufactoringProductionStore.loadedDateFrom > dateFrom);
        if (mfpNeedsLoad) {
            if (manufactoringProductionStore.isLoading) {
                loadPromises.push(waitForStore(manufactoringProductionStore));
            } else {
                loadPromises.push(manufactoringProductionStore.load({ page: 1, pageSize: 0, centerId, dateFrom }, perfPage));
            }
        }
        if (loadPromises.length > 0) {
            // C'est ici que se joue l'essentiel du temps : ces chargements ramènent les stores
            // ENTIERS (pageSize: 0) alors que l'écran n'affiche que les dispatches reçus.
            perfStart('Chargement stores Lot + Fabrication');
            await Promise.all(loadPromises);
            perfEnd('Chargement stores Lot + Fabrication', 'api');
        }

        // 3. Create Maps for O(1) lookups
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const lotMap = new Map(lotStore.datas.map(l => [l.id, l]));
        const manufactoringProductionMap = new Map(manufactoringProductionStore.datas.map(m => [m.id, m]));
        const dispatchMap = new Map<number, IDispatchManufactoring>();

        // Charger les ManufactoringProductions manquantes (doses d'un autre centre ou hors plage de dates).
        // « Manquante » vaut aussi pour une entrée présente mais sans sa production : le suivi de
        // fabrication lit le verrat, le schéma et la date de prélèvement à travers elle, et ne
        // vérifier que la présence laissait la ligne vide jusqu'au F5 (Mantis 0028321).
        const missingMfpIds = [...new Set(
            dispatches
                .map(d => d.manufactoringProductionId)
                .filter((id): id is number => !!id && !manufactoringProductionMap.get(id)?.production)
        )];
        // Charger les Lots manquants (lot hétérospermie créé sur un autre poste et reçu via
        // SignalR : le lotStore déjà chargé ne contient pas ce nouveau lot → belongsToLot serait null
        // → le dispatch serait exclu du suivi de fabrication, invisible jusqu'au F5). Mantis 0028085.
        const missingLotIds = [...new Set(
            dispatches
                .flatMap(d => [d.belongsToLotId, d.partOfLotId])
                .filter((id): id is number => !!id && !lotMap.has(id))
        )];

        // Les deux résolutions sont indépendantes : une requête `Ids=` chacune, en parallèle, au lieu
        // d'un GET par entité manquante puis d'une seconde vague séquentielle.
        perfStart('Résolution par ids (fabrications + lots)');
        const [loadedMfps, loadedLots] = await Promise.all([
            missingMfpIds.length > 0
                ? manufactoringProductionStore.ensureLoadedByIds(missingMfpIds, perfPage)
                : Promise.resolve([] as IManufactoringProduction[]),
            missingLotIds.length > 0
                ? lotStore.ensureLoadedByIds(missingLotIds, perfPage)
                : Promise.resolve([] as ILot[]),
        ]);
        perfEnd('Résolution par ids (fabrications + lots)', 'api');
        loadedMfps.forEach(m => manufactoringProductionMap.set(m.id, m));
        loadedLots.forEach(l => lotMap.set(l.id, l));

        // 4. First pass: create all entities without children
        const models: IDispatchManufactoring[] = [];
        for (const dispatch of dispatches) {
            const model = { ...dispatch } as unknown as IDispatchManufactoring;

            // Link references (O(1) via Map)
            model.center = centerMap.get(dispatch.centerId) ?? model.center;
            model.packaging = parameterMap.get(dispatch.packagingId) ?? model.packaging;
            model.belongsToLot = dispatch.belongsToLotId ? lotMap.get(dispatch.belongsToLotId) : undefined;
            model.partOfLot = dispatch.partOfLotId ? lotMap.get(dispatch.partOfLotId) : undefined;
            model.manufactoringProduction = dispatch.manufactoringProductionId ? manufactoringProductionMap.get(dispatch.manufactoringProductionId) : undefined;

            // Initialize children array
            model.children = [];

            models.push(model);
            dispatchMap.set(model.id, model);
        }

        // 5. Second pass: link parent/children relationships
        // model.parentId est déjà disponible via le spread ({ ...dispatch }) — pas besoin de dispatches.find() (O(n²))
        for (const model of models) {
            if (model.parentId) {
                model.parent = dispatchMap.get(model.parentId);
                if (model.parent) {
                    model.parent.children.push(model);
                }
            }
        }

        return models;
    }

    // Getters
    const getBelongsToLot = computed(() => {
        return (lotId: number): IDispatchManufactoring[] => {
            return datas.value.filter(d => d.belongsToLot?.id == lotId && d.isCanceled == false && d.isValidated == false);
        };
    });

    const getPartOfLot = computed(() => {
        return (lotId: number): IDispatchManufactoring[] => {
            return datas.value.filter(d => d.partOfLot?.id == lotId && d.isCanceled == false && d.isValidated == false);
        };
    });

    const getVolumeFabricationForProduction = computed(() => {
        return (lotId: number): number => {
            let volume = 0;
            const matching = datas.value.filter(d => d.belongsToLot?.id == lotId && d.isCanceled == false);
            matching.forEach(dispatchFabrication => {
                if (dispatchFabrication.volume > 0 && dispatchFabrication.numberDosesTheoretical > 0) {
                    volume += (dispatchFabrication.numberDosesTheoretical * dispatchFabrication.volume) + dispatchFabrication.primerVolume;
                }
            });
            return volume;
        };
    });

    const getVolumeFabricationForLot = computed(() => {
        return (lotId: number): number => {
            let volume = 0;
            datas.value.forEach(dispatchFabrication => {
                if (dispatchFabrication.partOfLot?.id == lotId && dispatchFabrication.volume > 0 && dispatchFabrication.numberDosesTheoretical > 0 && dispatchFabrication.isCanceled == false) {
                    volume += (dispatchFabrication.numberDosesTheoretical * dispatchFabrication.volume) + dispatchFabrication.primerVolume;
                }
            });
            return volume;
        };
    });

    const getPrevisionalStockProduction = computed(() => {
        return (idProduction: number, packagingCode: string, volumeDose: number, armada: boolean): number => {
            const dispatches = datas.value.filter((dispatch) => {
                const baseCondition = (
                    dispatch.manufactoringProduction?.production?.id === idProduction &&
                    dispatch.packaging?.code3 === packagingCode &&
                    dispatch.volume === volumeDose &&
                    dispatch.belongsToLot?.armada === armada &&
                    dispatch.isCanceled === false &&
                    dispatch.isValidated === false
                );

                const isValidForHetero = packagingCode === 'H'
                    ? !dispatch.partOfLot
                    : true;

                return baseCondition && isValidForHetero;
            });

            const stockPrevisionnel = dispatches.reduce((total, dispatch) => {
                return total - ((dispatch.numberDosesTheoretical || 0));
            }, 0);

            return stockPrevisionnel;
        };
    });

    // --- Getters prévisionnels : lisent depuis previsionalDatas (champs plats, pas d'hydratation) ---

    // Mantis 0028528 : les orientations d'un lot MONO n'ont pas non plus de production rattachée
    // (`hasManufactoringProduction` à false). Sans le filtre `isHeterospermie`, elles gonflaient
    // la ligne HETERO du tableau de dispatch.
    const getDispatchPrevisionnelHetero = computed(() => {
        return (schema: string, armada: boolean, volumeDose: number): GroupedStock[] => {
            return previsionalDatas.value
                .filter(d =>
                    d.isHeterospermie &&
                    d.geneticTypeCode === schema &&
                    d.volume === volumeDose &&
                    d.isArmada === armada &&
                    !d.hasPartOfLot &&
                    !d.hasManufactoringProduction
                )
                .map(d => ({
                    codeArticle: d.geneticTypeCode,
                    idArticle: 0,
                    libelleArticle: d.articleName,
                    centerCode: d.centerCode,
                    isArmada: d.isArmada,
                    isHeterospermie: d.isHeterospermie,
                    conditionnement: d.packagingCode,
                    conditionnementCode: d.packagingCode,
                    schemaCode: d.geneticTypeCode,
                    quantity: -(d.numberDosesTheoretical || 0),
                    volumeDose: d.volume,
                    spzDose: d.spzDose || 0,
                    stockUnity: {} as GroupedStock["stockUnity"],
                    stocks: [] as GroupedStock["stocks"],
                    isPrevisional: true
                }));
        };
    });

    const getDispatchPrevisionnal = computed(() => {
        return (schema: string, packagingCode: string, volumeDose: number, armada: boolean, isHetero: boolean): GroupedStock[] => {
            return previsionalDatas.value
                .filter(d => {
                    const baseCondition =
                        d.geneticTypeCode === schema &&
                        (packagingCode === '' || d.packagingCode === packagingCode) &&
                        d.volume === volumeDose &&
                        d.isArmada === armada &&
                        !d.hasPartOfLot;

                    // Mantis 0028528 : une demande MONO ne retient que les orientations mono. Les
                    // doses d'un lot hétéro orientées en Selfix ou en Vrac étaient comptées à la
                    // fois dans la ligne HETERO et dans la ligne mono de leur conditionnement.
                    const isValidForHetero = isHetero ? d.isHeterospermie : !d.isHeterospermie;
                    const isValidForMono = !isHetero && packagingCode !== ''
                        ? (d.hasManufactoringProduction || d.hasBelongsToLot)
                        : true;
                    const isValidHeteroMono = packagingCode === ''
                        ? (d.hasManufactoringProduction || d.hasBelongsToLot)
                        : true;

                    return baseCondition && isValidForHetero && isValidForMono && isValidHeteroMono;
                })
                .map(d => {
                    const dosesDisponibles = d.volume > 0
                        ? Math.floor((d.volumeAvailable ?? 0) / d.volume)
                        : 0;

                    return {
                        codeArticle: d.geneticTypeCode,
                        idArticle: 0,
                        libelleArticle: d.articleName,
                        centerCode: d.centerCode,
                        isArmada: d.isArmada,
                        isHeterospermie: d.isHeterospermie,
                        conditionnement: d.packagingCode,
                        conditionnementCode: d.packagingCode,
                        schemaCode: d.geneticTypeCode,
                        quantity: -dosesDisponibles,
                        volumeDose: d.volume,
                        spzDose: d.spzDose || 0,
                        stockUnity: {} as GroupedStock["stockUnity"],
                        stocks: [] as GroupedStock["stocks"],
                        isPrevisional: true
                    };
                });
        };
    });

    const getPrevisionalStock = computed(() => {
        return (codeArticle: string, packagingCode: string, volumeDose: number, armada: boolean, isHetero: boolean): number => {
            const dispatches = previsionalDatas.value.filter(d =>
                d.geneticTypeCode === codeArticle &&
                d.packagingCode === packagingCode &&
                d.volume === volumeDose &&
                d.isArmada === armada &&
                (!isHetero ? d.hasManufactoringProduction : true)
            );

            return dispatches.reduce((total, d) => total - (d.numberDosesTheoretical || 0), 0);
        };
    });

    const getListeFabricationToDispatch = computed((): IDispatchManufactoring[] => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        const normalizeDateMs = (date?: string | Date | null): number => {
            if (!date) return 0;
            const d = new Date(date);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        };

        const todayMs = today.getTime();
        const sevenDaysAgoMs = sevenDaysAgo.getTime();

        const isWithinLast7Days = (dateMs: number): boolean => {
            return dateMs >= sevenDaysAgoMs && dateMs <= todayMs;
        };

        // Passe O(n) : filtre + pré-calcul des clés de tri en une seule fois
        type WithSortKey = { item: IDispatchManufactoring; dateMs: number; name: string };
        const filtered: WithSortKey[] = [];
        for (const fabrication of datas.value) {
            let keep: boolean;
            if (recentOnly.value) {
                const validatedMs = normalizeDateMs(fabrication.dateValidation);
                keep = fabrication.isValidated && isWithinLast7Days(validatedMs);
            } else {
                keep = fabrication.packaging?.code3 !== 'H'
                    && !fabrication.isCanceled
                    && !fabrication.isValidated
                    // Exclure les contributions hétéro (partOfLotId sans manufactoringProduction ni belongsToLot)
                    && (fabrication.manufactoringProduction != null || fabrication.belongsToLot != null);
            }
            if (!keep) continue;

            filtered.push({
                item: fabrication,
                dateMs: normalizeDateMs(fabrication.manufactoringProduction?.production?.dateProduction),
                name: fabrication.manufactoringProduction?.production?.animal?.name?.toUpperCase() || '',
            });
        }

        // Tri O(n log n) sans recalcul de date/nom dans le comparateur
        filtered.sort((a, b) => {
            if (a.dateMs !== b.dateMs) return b.dateMs - a.dateMs;
            return a.name.localeCompare(b.name);
        });

        return filtered.map(x => x.item);
    });

    /**
     * Fabrications MONO (dispatches liés à une ManufactoringProduction) dont le DISPATCH — pas la
     * production — a eu lieu hier ou aujourd'hui. Sert à l'envoi de la "base du jour" aux appareils
     * (AGP3500/RQ800/PM43C) lors de l'orientation.
     *
     * Basé sur DateManufacturing (date du dispatch) plutôt que sur ManufactoringProduction.addingDate
     * (date de la production, cf. l'ancien todayAndYesterdayManufactures de manufactoringProduction.store) :
     * un verrat produit lundi mais dispatché seulement vendredi était absent de la base envoyée aux
     * machines toute la semaine, alors qu'il aurait dû y figurer le jour où il est réellement orienté.
     * DateManufacturing vaut systématiquement `new Date()` au moment de la création/sauvegarde du
     * dispatch dans ce store (createDispatchToDeleteLot, batchCreateDispatchesToDeleteLots, save,
     * batchSave) — c'est la date de l'ACTE de dispatch, pas une date métier saisie séparément.
     */
    const todayAndYesterdayDispatchedManufactures = computed((): IManufactoringProduction[] => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday);
        const startOfTomorrow = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

        const seen = new Map<number, IManufactoringProduction>();
        for (const dispatch of datas.value) {
            if (dispatch.isCanceled || !dispatch.manufactoringProduction) continue;
            if (!dispatch.dateManufactoring) continue;
            const dispatchDate = new Date(dispatch.dateManufactoring);
            if (dispatchDate < startOfYesterday || dispatchDate >= startOfTomorrow) continue;
            seen.set(dispatch.manufactoringProduction.id, dispatch.manufactoringProduction);
        }
        return [...seen.values()];
    });

    /**
     * Pendant hétérospermie de todayAndYesterdayDispatchedManufactures : Lots dont au moins un
     * dispatch (belongsToLot ou partOfLot — une contribution hétéro n'a que partOfLot renseigné)
     * a eu lieu hier ou aujourd'hui.
     */
    const todayAndYesterdayDispatchedLots = computed((): ILot[] => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfYesterday = new Date(startOfToday);
        const startOfTomorrow = new Date(startOfToday);
        startOfYesterday.setDate(startOfYesterday.getDate() - 1);
        startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);

        const seen = new Map<number, ILot>();
        for (const dispatch of datas.value) {
            if (dispatch.isCanceled) continue;
            const lot = dispatch.belongsToLot ?? dispatch.partOfLot;
            if (!lot) continue;
            if (!dispatch.dateManufactoring) continue;
            const dispatchDate = new Date(dispatch.dateManufactoring);
            if (dispatchDate < startOfYesterday || dispatchDate >= startOfTomorrow) continue;
            seen.set(lot.id, lot);
        }
        return [...seen.values()];
    });

    const getByManufactoringProductionId = computed((): IDispatchManufactoring[] => {
        return datas.value.filter((dispatch) => dispatch.manufactoringProduction?.id === filter.value.manufactoringProductionId && dispatch.isCanceled == false && dispatch.isValidated == false);
    });

    const getDispatchBelongingToLot = computed(() => {
        return (lotId: number) => {
            return datas.value.filter(d => d.partOfLot?.id == lotId);
        };
    });

    // Actions
    async function resendStock(centerCodeSpecies: string) {
        isLoading.value = true;
        let result = await DispatchManufactoringProductionServices.resendStock(centerCodeSpecies);
        isLoading.value = false;
        datas.value = [];
        return result;
    }

    async function load(loadFilter: IDispatchManufactoringFilter, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);

        filter.value = loadFilter;
        isLoading.value = true;

        // Pré-chargement des sous-stores en parallèle avec l'appel API principal.
        // lot et manufactoringProduction sont lazy-loaded (absents du storeLoader) :
        // sans ça, hydrateDispatchManufactorings() les chargerait séquentiellement après le GET.
        // Guard isLoading : évite le double-chargement si la vue charge déjà ces stores en parallèle.
        const lotStore = useLotStore();
        const manufactoringProductionStore = useManufactoringProductionStore();
        const subStorePromises: Promise<unknown>[] = [];
        const reqDateFrom = loadFilter.dateFrom;
        const lotNeedsLoad =
            lotStore.datas.length === 0 ||
            lotStore.loadedCenterId !== loadFilter.centerId ||
            (reqDateFrom === undefined && lotStore.loadedDateFrom !== undefined) ||
            (reqDateFrom !== undefined && lotStore.loadedDateFrom !== undefined && lotStore.loadedDateFrom > reqDateFrom);
        if (lotNeedsLoad && !lotStore.isLoading) {
            subStorePromises.push(lotStore.load({ page: 1, pageSize: 0, centerId: loadFilter.centerId, dateFrom: reqDateFrom }, loadFilter.centerId, perfPage));
        }
        const mfpNeedsLoad =
            manufactoringProductionStore.datas.length === 0 ||
            (reqDateFrom === undefined && manufactoringProductionStore.loadedDateFrom !== undefined) ||
            (reqDateFrom !== undefined && manufactoringProductionStore.loadedDateFrom !== undefined && manufactoringProductionStore.loadedDateFrom > reqDateFrom);
        if (mfpNeedsLoad && !manufactoringProductionStore.isLoading) {
            subStorePromises.push(manufactoringProductionStore.load({ page: 1, pageSize: 0, centerId: loadFilter.centerId, dateFrom: reqDateFrom }, perfPage));
        }

        start('GET /DispatchManufactoring (réseau)');
        const result = await DispatchManufactoringProductionServices.getAll<IDispatchManufactoringFilter, IDispatchManufactoringApiResponse>(loadFilter);
        end('GET /DispatchManufactoring (réseau)', 'api');

        // Attendre que les sous-stores soient prêts (généralement déjà terminés pendant le GET)
        if (subStorePromises.length > 0) {
            start('Pré-chargement sous-stores');
            await Promise.all(subStorePromises);
            end('Pré-chargement sous-stores', 'api');
        }

        // On remplace datas uniquement après hydratation pour éviter le flash visuel
        // (ancien : datas = [] avant l'await → grille brièvement vide à chaque rechargement)
        if (!result.error) {
            start('hydrateDispatchManufactorings()');
            datas.value = await hydrateDispatchManufactorings(result.data as IDispatchManufactoringApiResponse[], loadFilter.centerId, loadFilter.dateFrom, perfPage);
            end('hydrateDispatchManufactorings()', 'mapping');
        } else {
            datas.value = [];
        }
        loadedCenterId.value = loadFilter.centerId;
        loadedDateFrom.value = loadFilter.dateFrom;
        loadedViaComposite.value = false;
        isLoading.value = false;
        return result;
    }

    // Séquencement du prévisionnel. Trois déclencheurs (ouverture de la tuile, enregistrement, push SignalR)
    // peuvent se chevaucher : seule la dernière requête lancée a le droit d'écrire dans le state, une
    // réponse plus ancienne (succès ou erreur) arrivant en retard est ignorée.
    // L'horloge est un simple compteur d'évènements (lancement de requête, écriture constatée) : elle
    // ordonne strictement « cette requête est partie après cette écriture », sans ex aequo possible.
    let previsionalClock = 0;
    let previsionalLastRequestSeq = 0;
    let previsionalInFlight: { seq: number; centerId: number; startedAt: number; promise: Promise<void> } | null = null;
    let previsionalLastSuccess: { centerId: number; startedAt: number } | null = null;

    /** Marque une écriture (enregistrement local ou push SignalR) : renvoie l'instant à passer à loadPrevisional. */
    function markPrevisionalWrite(): number {
        return ++previsionalClock;
    }

    /**
     * Charge le prévisionnel du centre.
     * @param writtenAt instant d'une écriture que le résultat doit refléter (markPrevisionalWrite). Sans lui
     * (ouverture de la tuile), une requête déjà en vol pour le centre est réutilisée. Avec lui, on ne réutilise
     * une requête en vol, ou on ne se contente du dernier chargement réussi, que s'ils sont partis APRÈS
     * l'écriture ; une requête partie avant ne la voit peut-être pas, on en relance une (qui rend l'ancienne
     * obsolète). Ainsi l'enregistrement et le push SignalR qui le suit ne déclenchent qu'un seul GET quand
     * le push est reçu avant le départ du rechargement, sans jamais servir un résultat antérieur à l'écriture.
     */
    function loadPrevisional(centerId: number, writtenAt?: number): Promise<void> {
        if (writtenAt !== undefined && previsionalLastSuccess?.centerId === centerId
            && previsionalLastSuccess.startedAt > writtenAt && previsionalLoadedCenterId.value === centerId
            && (!previsionalInFlight || previsionalInFlight.centerId === centerId)) {
            return previsionalInFlight?.promise ?? Promise.resolve();
        }
        if (previsionalInFlight?.centerId === centerId
            && (writtenAt === undefined || previsionalInFlight.startedAt > writtenAt)) {
            return previsionalInFlight.promise;
        }

        const seq = ++previsionalLastRequestSeq;
        const startedAt = ++previsionalClock;
        const promise = (async () => {
            try {
                const result = await DispatchManufactoringProductionServices.getPrevisionalStock(centerId);
                if (seq !== previsionalLastRequestSeq) return;
                // En erreur, on garde le prévisionnel précédent s'il se rapporte au même centre : le vider ferait
                // réapparaître toutes les commandes déjà orientées dans les besoins. S'il porte sur un autre centre,
                // on le vide : les getters ne contrôlent pas previsionalLoadedCenterId et l'afficheraient tel quel.
                if (result.error) {
                    if (centerId !== previsionalLoadedCenterId.value) {
                        previsionalDatas.value = [];
                        previsionalLoadedCenterId.value = undefined;
                    }
                    return;
                }
                previsionalDatas.value = result.data ?? [];
                previsionalLoadedCenterId.value = centerId;
                previsionalLastSuccess = { centerId, startedAt };
            } finally {
                if (previsionalInFlight?.seq === seq) previsionalInFlight = null;
            }
        })();
        previsionalInFlight = { seq, centerId, startedAt, promise };
        return promise;
    }

    async function get(id: number) {
        const result = await DispatchManufactoringProductionServices.getById<IDispatchManufactoringApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse]);

            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function createDispatchToDeleteLot(lot: ILot, volumeRestant: number, centerId: number, volumeDose = 40, packagingDefaultId = 0) {
        try {
            if (!lot || volumeRestant <= 0) {
                throw new Error(i18n.global.t('errors.lotNotFoundOrVolume', { id: lot.id }));
            }

            const nombreDosesNecessaires = Math.ceil(volumeRestant / volumeDose);

            const dispatchForm: IDispatchManufactoringForm = {
                id: 0,
                volume: volumeDose,
                parentId: undefined,
                manufactoringProductionId: undefined,
                packagingId: packagingDefaultId,
                numberDosesManufactured: nombreDosesNecessaires,
                numberDosesTheoretical: nombreDosesNecessaires,
                numberManufactoring: -1,
                dateManufactoring: new Date(),
                isArmada: lot.armada || false,
                datePackaging: new Date(),
                isValidated: true,
                isCanceled: false,
                belongsToLotId: lot.id,
                partOfLotId: undefined,
                primerVolume: 0,
                centerId: centerId || 0,
                archived: false,
            };

            itemForm.value = dispatchForm;
            const result = await save();
            return result;

        } catch (error) {
            console.error(`Erreur lors de la création du dispatch de suppression pour le lot ${lot.id}:`, error);

            const errorResult = new ServiceOperationResult<IDataOperationResult<IDispatchManufactoring>>(
                true,
                { errors: new DataOperationError() }
            );
            return errorResult;
        }
    }

    /**
     * Crée en batch les dispatches de suppression pour une liste de lots.
     * Envoie une seule requête HTTP au lieu d'une boucle séquentielle,
     * ce qui évite les mises à jour SignalR intermédiaires et les conflits sur itemForm partagé.
     */
    async function batchCreateDispatchesToDeleteLots(lots: { lot: ILot, volume: number }[], centerId: number, volumeDose = 40, packagingDefaultId = 0) {
        const forms: IDispatchManufactoringForm[] = lots
            .filter(({ lot, volume }) => lot && volume > 0)
            .map(({ lot, volume }) => {
                const nombreDosesNecessaires = Math.ceil(volume / volumeDose);
                return {
                    id: 0,
                    volume: volumeDose,
                    parentId: undefined,
                    manufactoringProductionId: undefined,
                    packagingId: packagingDefaultId,
                    numberDosesManufactured: nombreDosesNecessaires,
                    numberDosesTheoretical: nombreDosesNecessaires,
                    numberManufactoring: -1,
                    dateManufactoring: new Date(),
                    isArmada: lot.armada || false,
                    datePackaging: new Date(),
                    isValidated: true,
                    isCanceled: false,
                    belongsToLotId: lot.id,
                    partOfLotId: undefined,
                    primerVolume: 0,
                    centerId: centerId || 0,
                    archived: false,
                } as IDispatchManufactoringForm;
            });

        if (forms.length === 0) return;
        return await batchSave(forms);
    }

    /**
     * Sauvegarde un dispatch à partir d'un formulaire passé en paramètre.
     * Contrairement à save(), n'utilise pas l'état partagé itemForm/current,
     * ce qui permet d'appeler cette méthode en parallèle sans race condition.
     */
    async function saveDispatch(form: IDispatchManufactoringForm) {
        const lotStore = useLotStore();
        const result = await DispatchManufactoringProductionServices.update(form);
        if (!result.error) {
            const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse], loadedCenterId.value, lotStore.loadedDateFrom);
            const index = datas.value.findIndex((item) => item.id === form.id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
        }
        return result;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        const lotStore = useLotStore();
        let result: ServiceOperationResult<IDispatchManufactoring | IDataOperationResult<IDispatchManufactoring>>;
        if (itemForm.value.id > 0) {
            result = await DispatchManufactoringProductionServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse], loadedCenterId.value, lotStore.loadedDateFrom);
                current.value = modelData;

                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                    return new ServiceOperationResult<IDataOperationResult<IDispatchManufactoring>>(true, { errors: error });
                }
            }
        } else {
            result = await DispatchManufactoringProductionServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse], loadedCenterId.value, lotStore.loadedDateFrom);
                current.value = modelData;
            }
        }
        return result;
    }

    async function batchSave(forms: IDispatchManufactoringForm[]) {
        const lotStore = useLotStore();
        const created = forms.filter(f => f.id == 0);
        const updated = forms.filter(f => f.id > 0);

        const results: IDispatchManufactoring[] = [];
        // Au moins une écriture persistée : le prévisionnel doit être rechargé, y compris sur les
        // retours anticipés (batchUpdate en erreur après les créations, ligne absente du store).
        let persisted = false;
        let writtenAt = 0;

        try {
            if (created.length > 0) {
                const result = await DispatchManufactoringProductionServices.batchCreate(created);
                if (result.error) return;
                persisted = true;
                writtenAt = markPrevisionalWrite();

                const hydratedData = await hydrateDispatchManufactorings(result.data as IDispatchManufactoringApiResponse[], loadedCenterId.value, lotStore.loadedDateFrom);
                for (const dm of hydratedData) {
                    // Un id déjà présent n'est pas forcément un doublon : SignalR peut avoir devancé la
                    // réponse HTTP, et une orientation hétérospermie cumulée renvoie la ligne existante
                    // enrichie (Mantis 0028318). Dans les deux cas la réponse fait foi.
                    const existingIndex = datas.value.findIndex((a) => a.id == dm.id);
                    if (existingIndex == -1) {
                        datas.value.push(dm);
                    } else {
                        datas.value.splice(existingIndex, 1, dm);
                    }
                    results.push(dm);
                }
            }

            if (updated.length > 0) {
                const result = await DispatchManufactoringProductionServices.batchUpdate(updated);
                if (result.error) return;
                persisted = true;
                writtenAt = markPrevisionalWrite();

                const hydratedData = await hydrateDispatchManufactorings(result.data as IDispatchManufactoringApiResponse[], loadedCenterId.value, lotStore.loadedDateFrom);
                for (const dm of hydratedData) {
                    const existingIndex = datas.value.findIndex(edm => edm.id == dm.id);

                    if (existingIndex == -1) {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                        return new ServiceOperationResult<IDataOperationResult<IDispatchManufactoring>>(true, { errors: error });
                    }

                    datas.value[existingIndex] = dm;
                    results.push(dm);
                }
            }

            return results;
        } finally {
            // Le prévisionnel des besoins vient d'une projection à part : sans ce rechargement, il ne
            // suivait l'enregistrement que par le push SignalR (Mantis 0028655). Lancé sans être attendu :
            // l'appelant n'a pas à patienter pour une projection secondaire, et une erreur ne doit pas
            // remonter comme un échec de l'enregistrement.
            if (persisted && previsionalLoadedCenterId.value) {
                loadPrevisional(previsionalLoadedCenterId.value, writtenAt).catch((error) => {
                    console.error("Rechargement du prévisionnel après enregistrement", error);
                });
            }
        }
    }

    function toForm(item: IDispatchManufactoring): IDispatchManufactoringForm {
        const form = {} as IDispatchManufactoringForm;
        Object.assign(form, item);
        form.manufactoringProductionId = item.manufactoringProductionId;
        form.packagingId = item.packagingId;
        form.belongsToLotId = item.belongsToLotId;
        form.partOfLotId = item.partOfLotId;
        form.parentId = item.parentId;
        form.centerId = item.centerId;
        return form;
    }

    function newForm(lienId?: number): IDispatchManufactoringForm {
        itemForm.value = {
            id: 0,
            volume: 0,
            parentId: undefined,
            manufactoringProductionId: lienId,
            packagingId: 0,
            numberDosesManufactured: 0,
            numberDosesTheoretical: 0,
            numberManufactoring: 0,
            dateManufactoring: new Date(),
            isArmada: false,
            datePackaging: new Date(),
            isValidated: false,
            isCanceled: false,
            belongsToLotId: undefined,
            partOfLotId: undefined,
            primerVolume: 0,
            centerId: 0,
            archived: false,
        } as IDispatchManufactoringForm;
        return itemForm.value;
    }

    function newItem(lien: IManufactoringProduction | undefined): IDispatchManufactoring {
        current.value = {
            id: 0,
            parent: undefined,
            children: [],
            manufactoringProduction: lien,
            manufactoringProductionId: lien?.id,
            volume: 0,
            numberDosesManufactured: 0,
            numberDosesTheoretical: 0,
            numberManufactoring: 0,
            packaging: { id: 0 } as IParameter,
            packagingId: 0,
            datePackaging: new Date(),
            dateManufactoring: new Date(),
            isValidated: false,
            isCanceled: false,
            belongsToLot: undefined,
            partOfLot: undefined,
            isArmada: false,
            primerVolume: 0,
            center: { id: 0 } as ICenter,
            centerId: 0,
            archived: false,
            volumeAvailable: 0,
        } as IDispatchManufactoring;
        return current.value;
    }

    return {
        // State
        isLoading,
        datas,
        current,
        itemForm,
        filter,
        recentOnly,
        loadedCenterId,
        loadedDateFrom,
        loadedViaComposite,
        previsionalDatas,
        previsionalLoadedCenterId,
        signalRVersion,
        // Getters
        getBelongsToLot,
        getPartOfLot,
        getVolumeFabricationForProduction,
        getVolumeFabricationForLot,
        getPrevisionalStockProduction,
        getDispatchPrevisionnelHetero,
        getDispatchPrevisionnal,
        getPrevisionalStock,
        getListeFabricationToDispatch,
        todayAndYesterdayDispatchedManufactures,
        todayAndYesterdayDispatchedLots,
        getByManufactoringProductionId,
        getDispatchBelongingToLot,
        // Actions
        hydrateDispatchManufactorings,
        resendStock,
        load,
        loadPrevisional,
        markPrevisionalWrite,
        get,
        searchStoreById,
        createDispatchToDeleteLot,
        batchCreateDispatchesToDeleteLots,
        saveDispatch,
        save,
        batchSave,
        toForm,
        newForm,
        newItem
    };
});

// Batch les événements SignalR pour éviter N hydratations + N recalculs de computed
// quand orient-hetero crée plusieurs dispatches d'un coup
let signalRPendingPayloads: IPayloadStore[] = [];
let signalRFlushTimer: ReturnType<typeof setTimeout> | null = null;
// Instant (markPrevisionalWrite) du dernier push reçu : le rechargement du prévisionnel doit partir après lui
let signalRLastWriteAt = 0;

async function flushSignalRBatch() {
    const payloads = signalRPendingPayloads;
    const writtenAt = signalRLastWriteAt;
    signalRPendingPayloads = [];
    signalRFlushTimer = null;

    if (payloads.length === 0) return;

    const store = useDispatchManufactoringStore();
    const lotStore = useLotStore();
    const centerId = store.loadedCenterId;
    const dateFrom = lotStore.loadedDateFrom;

    // Séparer creates, updates, deletes
    const creates: IDispatchManufactoringApiResponse[] = [];
    const updates: IDispatchManufactoringApiResponse[] = [];
    const deleteIds: number[] = [];

    for (const payload of payloads) {
        switch (payload.action) {
            case "Create":
                if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1)
                    creates.push(payload.data as IDispatchManufactoringApiResponse);
                break;
            case "Update":
                if (store.datas.findIndex((a) => a.id == payload.data?.id) > -1)
                    updates.push(payload.data as IDispatchManufactoringApiResponse);
                else
                    // Dispatch pas dans le store mais du même centre → l'ajouter (ex: renvoi depuis un autre onglet)
                    creates.push(payload.data as IDispatchManufactoringApiResponse);
                break;
            case "Delete":
                if (payload.id) deleteIds.push(payload.id);
                break;
        }
    }

    // Hydrater en batch (1 seul appel au lieu de N)
    if (creates.length > 0) {
        const hydrated = await store.hydrateDispatchManufactorings(creates, centerId, dateFrom);
        for (const dm of hydrated) {
            if (store.datas.findIndex((a) => a.id == dm.id) == -1)
                store.datas.push(dm);
        }
    }

    if (updates.length > 0) {
        const hydrated = await store.hydrateDispatchManufactorings(updates, centerId, dateFrom);
        for (const dm of hydrated) {
            const idx = store.datas.findIndex((a) => a.id == dm.id);
            if (idx > -1) store.datas.splice(idx, 1, dm);
        }
    }

    for (const id of deleteIds) {
        const idx = store.datas.findIndex((a) => a.id == id);
        if (idx > -1) store.datas.splice(idx, 1);
    }

    // Recharger les données prévisionnelles (projection SQL légère). Réutilise le rechargement lancé par
    // batchSave s'il est parti après la réception du push : pas de second GET pour la même écriture.
    if (store.previsionalLoadedCenterId) {
        await store.loadPrevisional(store.previsionalLoadedCenterId, writtenAt);
    }

    // Incrémenter le compteur pour forcer la réévaluation des computed externes
    // (ex: computedRowData dans EjaculateListView)
    store.signalRVersion++;
}

signalRManager.connection.on("MajDispatchManufactoring", async (payload: IPayloadStore) => {
    const store = useDispatchManufactoringStore();
    // Ignorer les événements d'autres centres
    if (store.loadedCenterId !== undefined && payload.data?.centerId !== undefined
        && payload.data.centerId !== store.loadedCenterId) {
        return;
    }

    signalRPendingPayloads.push(payload);
    signalRLastWriteAt = store.markPrevisionalWrite();
    if (signalRFlushTimer) clearTimeout(signalRFlushTimer);
    signalRFlushTimer = setTimeout(flushSignalRBatch, 200);
});
